import {Injectable} from '@angular/core';
import {AngularFirestore, QueryFn} from '@angular/fire/compat/firestore';
import {forkJoin, from, Observable} from 'rxjs';
import {first, map} from 'rxjs/operators';
import {readCollectionWithIds, readDocumentWithId} from '../common/firestore-utils';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {TenantService} from './tenant.service';
import {User} from '../models/user.model';
import * as uniq from 'uniq';
import firebase from 'firebase/compat/app';


@Injectable({
  providedIn: 'root'
})
export class SchoolUsersDbService {

  constructor(
    private afs: AngularFirestore,
    private afAuth: AngularFireAuth,
    private tenant: TenantService) {

  }

  loadUsersPageByEmail(pageSize: number, startAfter?: string): Observable<User[]> {
    const queryFn: QueryFn = ref => {
      let query = ref.orderBy('email', 'asc').limit(pageSize);
      if (startAfter) {
        query = query.startAfter(startAfter);
      }
      return query;
    };

    return readCollectionWithIds<User[]>(this.afs.collection(`/schools/${this.tenant.id}/users`, queryFn));
  }

  async saveUserProfileIfNeeded(tenantId:string, userId: string, email: string, pictureUrl: string, displayName: string) {

    const userPath = `schools/${tenantId}/users/${userId}`

    console.log(`Checking if profile of user ${userId} needs to be saved: ${userPath} `);

    const user = await this.afs.doc(userPath).get().pipe(first()).toPromise();

    const userData = user.data() as User;

    console.log(`returned user data: ${JSON.stringify(userData)}`);

    if (userData?.email) {
      console.log(`User with email found, skipping profile save.`);
      return null;
    }

    console.log(`User with email not found, creating new user profile.`);

    const addUserAsync = this.afs
      .doc(userPath)
      .set({
        email,
        pictureUrl: pictureUrl || null,
        displayName: displayName || null,
      }, {merge: true});

    return addUserAsync;
  }

  findUserByUid(uid: string): Observable<User> {
    return forkJoin(
      [
        readDocumentWithId<User>(this.afs.doc(`schools/${this.tenant.id}/users/${uid}`)).pipe(first()),
        readDocumentWithId<User>(this.afs.doc(`schools/${this.tenant.id}/usersPrivate/${uid}`)).pipe(first())
      ])
      .pipe(
        map(([data, privateData]) => {
          return {
            id:uid,
            ...data,
            ...privateData
          };
        })
      );
  }

  loadUserPrivate(tenantId: string, userId: string): Observable<{ purchasedCourses: string[] }> {
    return this.afs.doc(`schools/${tenantId}/usersPrivate/${userId}`)
      .get()
      .pipe(
        map(snap => {
          const userPrivate = snap.data() as User;
          if (!snap.exists) {
            return {
              purchasedCourses: [],
            };
          }
          if (userPrivate.purchasedCourses) {
            uniq(userPrivate.purchasedCourses);
          }
          return {
            purchasedCourses: userPrivate.purchasedCourses || [],
          };
        })
      );
  }

  updateUser(userId: string, props: Partial<User>) {
    const path = `schools/${this.tenant.id}/users/${userId}`;
    console.log(`Updating user in path ${path}`);
    const updateUserAsync = this.afs
      .doc(path)
      .set(props, {merge: true});
    return from(updateUserAsync);
  }

  updateUserPrivate(userId: string, props: any) {
    // this will only work for admins
    const updateUserAsync = this.afs
      .doc(`schools/${this.tenant.id}/usersPrivate/${userId}`)
      .set(props, {merge: true});
    return from(updateUserAsync);
  }

  updateNewsletterStatus(userId: string, val: boolean) {
    const updateUserAsync = this.afs
      .doc(`schools/${this.tenant.id}/users/${userId}`)
      .update({unsubscribeFromNewsletter: val});
    return from(updateUserAsync);
  }

  findUserByTenantAndId(tenantId: string, userId: string) {
    return readDocumentWithId<User>(this.afs.doc(`schools/${tenantId}/users/${userId}`)).pipe(first());
  }

  saveLastSeenAt(tenantId: string, userId:string) {

    const userPath = `schools/${tenantId}/users/${userId}`

    const updateUserAsync = this.afs
      .doc(userPath)
      .set({
        invitationStatus: "active",
        lastSeenAt: firebase.firestore.Timestamp.now()
      }, {merge: true});

    return updateUserAsync;
  }

}
